import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'

/**
 * The dropdown look of the estimate/invoice redesign prototype, shared by every menu in the
 * `estimateNext` editor (ENG-1919).
 *
 * The prototype draws all of its menus from one surface primitive: a 12px-radius white card with a
 * hairline border and a two-layer shadow, holding 6px-inset rows that each round to 6px and take a
 * wash on hover. Material's own menu is square-cornered, full-bleed, and anchors *over* its trigger,
 * so a call site opting in needs all three of the pieces below — the classes, the placement, and the
 * gap.
 *
 * Every value is the prototype's, mapped onto the Brand Neutral scale where the two agree exactly
 * (`#c6cedc` = P85, `#e8ebf1` = P94, `#d9dee8` = P90, `#55647c` = P50). Two do not sit on the scale:
 * the row wash `#f0f3f7` reads as P96, and the detail grey `#96a5b9` as P70.
 *
 * Use it through `ActionButton`'s `nextMenu` prop, or by hand for a `Menu` that has no ActionButton
 * (see `QuoteNextActionsMenu`).
 */
export const useNextMenuStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      // The menu card. `minWidth` is the prototype's 200; the max keeps a long label (e.g. "Convert
      // to Line Item") from stretching the card past its 280 ceiling.
      paper: {
        minWidth: 200,
        maxWidth: 280,
        backgroundColor: neutral.White,
        border: `0.5px solid ${neutral.P85}`,
        borderRadius: 12,
        boxShadow: '0 1px 2px rgba(20,30,50,0.10), 0 12px 32px rgba(20,30,50,0.20)'
      },
      // The prototype's 6px gap to the trigger, for a menu that drops below one. `anchorOrigin`
      // alone puts the card flush against the trigger's bottom edge.
      paperBelow: {
        marginTop: 6
      },
      // Same gap for a submenu that flies out to the right of its parent row.
      paperRight: {
        marginLeft: 6
      },
      // 6px all round, so the first/last row's rounded corner sits inside the card's own radius
      // instead of against it.
      list: {
        padding: 6
      },
      // One row. The icon is pulled to the prototype's 16px at an 8px gap — Material's MenuItem ships
      // a 24px glyph with a 16px gutter, which reads as a much heavier list.
      item: {
        minHeight: 36,
        padding: '8px 10px',
        borderRadius: 6,
        fontSize: theme.typography.pxToRem(13),
        lineHeight: 1.4,
        color: neutral.P30,
        '&:hover': {
          backgroundColor: neutral.P96
        },
        // width/height as well as fontSize: a plain inlined `<svg>` (as against a MUI `SvgIcon`) has
        // no intrinsic size and would otherwise stretch to the full width of the row.
        '& > svg:first-child, & > .MuiSvgIcon-root:first-child': {
          fontSize: theme.typography.pxToRem(16),
          width: '1em',
          height: '1em',
          flexShrink: 0,
          marginRight: theme.spacing(1),
          color: neutral.P70
        },
        // Touch targets get the prototype's taller row rather than the desktop 36.
        [theme.breakpoints.down('xs')]: {
          minHeight: 44,
          padding: '11px 10px',
          fontSize: theme.typography.pxToRem(15)
        }
      },
      /**
       * The prototype's footer row: the last entry of a menu, set apart by a light fill so it reads
       * as the menu's own control rather than one more action on the document (its Settings row).
       * Applied alongside {@link item}, and declared after it so it wins on source order.
       *
       * Full-bleed, unlike every other row. The negative margins cancel {@link list}'s 6px inset on
       * three sides so the fill runs to the card's own edges, and the bottom corners pick up the
       * card's 12px radius in place of the 6px a normal row rounds to. It draws its own top hairline
       * rather than taking an inset `<Divider>`, which would leave a gap beside the fill.
       */
      footerItem: {
        marginTop: 6,
        marginBottom: -6,
        marginLeft: -6,
        marginRight: -6,
        minHeight: 44,
        // 16px in, so the label still lines up with the inset rows above (their 6px + 10px).
        padding: '12px 16px',
        borderRadius: '0 0 12px 12px',
        borderTop: `1px solid ${neutral.P94}`,
        backgroundColor: neutral.P98,
        '&:hover': {
          backgroundColor: neutral.P96
        }
      },
      // Inset hairline between groups, rather than Material's full-bleed rule.
      divider: {
        margin: theme.spacing(0.75, 0.75),
        backgroundColor: neutral.P94
      },
      // A row that opens a submenu: label left, caret hard right.
      submenuItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: theme.spacing(2)
      },
      submenuArrow: {
        fontSize: theme.typography.pxToRem(16),
        color: neutral.P70
      }
    }
  },
  { name: 'NextMenu' }
)

/**
 * Placement for a menu hanging off a trigger: below it, right edges aligned.
 *
 * Material anchors a menu over its trigger by default (it lines the selected row up with the
 * button), which is what ENG-1921 reports as the Add dropdown covering the + Add button. Pinning
 * `getContentAnchorEl` to null is what lets `anchorOrigin` take effect at all.
 */
export const NEXT_MENU_PLACEMENT = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
  transformOrigin: { vertical: 'top', horizontal: 'right' }
} as const

/**
 * Placement for a submenu: to the **right** of the row that opened it, top edges aligned — the
 * direction its caret points (ENG-1920). Material's default for these was
 * `anchorOrigin: left / transformOrigin: right`, which put the flyout on the opposite side from
 * the arrow.
 */
export const NEXT_SUBMENU_PLACEMENT = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: 'top', horizontal: 'right' },
  transformOrigin: { vertical: 'top', horizontal: 'left' }
} as const
